import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { DEFAULT_SITE } from '../siteDefaults';
import {
  createGalleryItem,
  deleteGalleryItem,
  fetchSite,
  getToken,
  patchGallerySection,
  saveSite,
  updateGalleryItem,
  uploadFile,
} from './api';
import {
  AdminImagePreview,
  AdminModal,
  emptyGalleryItem,
  fileInput,
  input,
  label,
} from './adminUi';
import GalleryForm from './GalleryForm';

const secondaryBtnClass =
  'rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm font-medium text-neutral-800 hover:bg-neutral-50';
const primaryForestClass =
  'rounded-lg bg-brand-forest px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50';
const dangerBtnClass =
  'rounded-lg border border-red-200 bg-white px-3 py-2 text-sm font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50';

function ModalFooter({ onCancel, onSave, saveLabel, saving }) {
  return (
    <div className="flex flex-wrap justify-end gap-2">
      <button type="button" onClick={onCancel} className={secondaryBtnClass} disabled={saving}>
        Cancel
      </button>
      <button type="button" onClick={onSave} disabled={saving} className={primaryForestClass}>
        {saving ? 'Saving…' : saveLabel}
      </button>
    </div>
  );
}

function ensureGalleryShape(site) {
  const d = DEFAULT_SITE.gallery;
  if (site.gallery && Array.isArray(site.gallery.items)) {
    if (typeof site.gallery.pageHeroImage === 'string') return site;
    return {
      ...site,
      gallery: {
        ...site.gallery,
        pageHeroImage: typeof d.pageHeroImage === 'string' ? d.pageHeroImage : '',
      },
    };
  }
  return {
    ...site,
    gallery: {
      pageHeroImage: typeof d.pageHeroImage === 'string' ? d.pageHeroImage : '',
      sectionLabel: d.sectionLabel,
      sectionTitle: d.sectionTitle,
      intro: d.intro,
      items: [...(d.items || [])],
    },
  };
}

function ensureAdminSiteShape(site) {
  const s = ensureGalleryShape(site);
  return {
    ...DEFAULT_SITE,
    ...s,
    news:
      s.news && typeof s.news === 'object' && Array.isArray(s.news.articles) ? s.news : DEFAULT_SITE.news,
    footer: s.footer && typeof s.footer === 'object' ? s.footer : DEFAULT_SITE.footer,
    products:
      s.products && typeof s.products === 'object' && Array.isArray(s.products?.items)
        ? s.products
        : DEFAULT_SITE.products,
    gallery: s.gallery,
  };
}

function albumOptionsFromSite(site) {
  if (!site?.gallery) return [];
  const configured = Array.isArray(site.gallery.albums)
    ? site.gallery.albums.map((x) => (typeof x === 'string' ? x.trim() : '')).filter(Boolean)
    : [];
  const fromItems = Array.isArray(site.gallery.items)
    ? site.gallery.items.map((row) => (row?.album || '').trim()).filter(Boolean)
    : [];
  return [...new Set([...configured, ...fromItems])];
}

export default function GalleryDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [site, setSite] = useState(null);
  const siteRef = useRef(null);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);

  const [itemModalOpen, setItemModalOpen] = useState(false);
  const [itemModalMode, setItemModalMode] = useState(null);
  const [editGalleryItemId, setEditGalleryItemId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [modalErr, setModalErr] = useState('');
  const [modalSaving, setModalSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [uploadingHero, setUploadingHero] = useState(false);

  siteRef.current = site;

  const albumOptions = useMemo(() => albumOptionsFromSite(site), [site]);

  useEffect(() => {
    if (!getToken()) {
      navigate('/admin', { replace: true });
      return;
    }
    fetchSite()
      .then((data) => setSite(ensureAdminSiteShape(data)))
      .catch(() => setSite(ensureAdminSiteShape(JSON.parse(JSON.stringify(DEFAULT_SITE)))));
  }, [navigate]);

  const openCreateModal = useCallback(() => {
    setModalErr('');
    setDraft(emptyGalleryItem());
    setEditGalleryItemId(null);
    setItemModalMode('create');
    setItemModalOpen(true);
  }, []);

  const openEditModal = useCallback((row) => {
    if (!row) return;
    setModalErr('');
    setDraft({ ...row });
    setEditGalleryItemId(row.id);
    setItemModalMode('edit');
    setItemModalOpen(true);
  }, []);

  const closeItemModal = useCallback(() => {
    setItemModalOpen(false);
    setItemModalMode(null);
    setEditGalleryItemId(null);
    setDraft(null);
    setModalErr('');
    setModalSaving(false);
    setUploadingImage(false);
  }, []);

  useEffect(() => {
    if (!site) return;
    const s = location.state;
    if (!s || typeof s !== 'object') return;
    if (s.openNewGalleryItem) {
      openCreateModal();
      navigate(location.pathname, { replace: true, state: {} });
    } else if (typeof s.editGalleryItemId === 'string' && s.editGalleryItemId) {
      const id = s.editGalleryItemId;
      const row = site.gallery.items.find((x) => x.id === id);
      if (row) openEditModal(row);
      else setMsg('Image not found.');
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [site, location.pathname, location.state, navigate, openCreateModal, openEditModal]);

  function updateGallery(field, value) {
    setSite((s) => ({
      ...s,
      gallery: { ...s.gallery, [field]: value },
    }));
  }

  async function onHeroUpload(file) {
    if (!file) return;
    setMsg('');
    setErr('');
    setUploadingHero(true);
    try {
      const { url } = await uploadFile(file);
      updateGallery('pageHeroImage', url);
    } catch (e) {
      setErr(e.message || 'Upload failed');
    } finally {
      setUploadingHero(false);
    }
  }

  async function onModalUpload(file) {
    if (!file) return;
    setModalErr('');
    setUploadingImage(true);
    try {
      const { url } = await uploadFile(file);
      setDraft((x) => (x ? { ...x, src: url } : x));
    } catch (e) {
      setModalErr(e.message || 'Upload failed');
    } finally {
      setUploadingImage(false);
    }
  }

  async function handleModalSave() {
    if (!draft) return;
    const src = String(draft.src || '').trim();
    const album = String(draft.album || '').trim();
    if (!src) {
      setModalErr('Image URL is required (paste a URL or upload).');
      return;
    }
    if (!album) {
      setModalErr('Album is required. Configure albums under Gallery → Create album if the list is empty.');
      return;
    }
    const payload = {
      id: draft.id,
      src,
      alt: String(draft.alt || '').trim(),
      title: String(draft.title || '').trim(),
      album,
    };
    setModalErr('');
    setModalSaving(true);
    try {
      if (itemModalMode === 'create') {
        const data = await createGalleryItem(payload);
        setSite((s) => ensureAdminSiteShape({ ...s, gallery: data.gallery }));
        setMsg('Image added.');
      } else if (itemModalMode === 'edit' && editGalleryItemId) {
        const data = await updateGalleryItem(editGalleryItemId, payload);
        setSite((s) => ensureAdminSiteShape({ ...s, gallery: data.gallery }));
        setMsg('Image updated.');
      }
      closeItemModal();
    } catch (e) {
      setModalErr(e.message || 'Save failed');
    } finally {
      setModalSaving(false);
    }
  }

  async function handleModalDelete() {
    if (!editGalleryItemId) return;
    if (!window.confirm('Remove this image from the gallery?')) return;
    setModalSaving(true);
    setModalErr('');
    try {
      const data = await deleteGalleryItem(editGalleryItemId);
      setSite((s) => ensureAdminSiteShape({ ...s, gallery: data.gallery }));
      setMsg('Image removed.');
      closeItemModal();
    } catch (e) {
      setModalErr(e.message || 'Remove failed');
    } finally {
      setModalSaving(false);
    }
  }

  async function removeItem(row, rowIndex) {
    if (!window.confirm('Remove this image from the gallery?')) return;
    setMsg('');
    setErr('');
    setSaving(true);
    try {
      if (row?.id) {
        const data = await deleteGalleryItem(row.id);
        setSite((s) => ensureAdminSiteShape({ ...s, gallery: data.gallery }));
        setMsg('Image removed.');
      } else {
        const s = siteRef.current;
        if (!s) throw new Error('No site state');
        const nextSite = {
          ...s,
          gallery: { ...s.gallery, items: s.gallery.items.filter((_, j) => j !== rowIndex) },
        };
        setSite(nextSite);
        const updated = await saveSite(ensureAdminSiteShape(nextSite));
        setSite(ensureAdminSiteShape(updated));
        setMsg('Image removed.');
      }
    } catch (e) {
      setErr(e.message || 'Remove failed');
      try {
        const data = await fetchSite();
        setSite(ensureAdminSiteShape(data));
      } catch {
        /* ignore */
      }
    } finally {
      setSaving(false);
    }
  }

  async function save() {
    const cur = siteRef.current;
    if (!cur?.gallery) return;
    setMsg('');
    setErr('');
    setSaving(true);
    try {
      const data = await patchGallerySection({
        pageHeroImage: cur.gallery.pageHeroImage || '',
        sectionLabel: cur.gallery.sectionLabel,
        sectionTitle: cur.gallery.sectionTitle,
        intro: cur.gallery.intro,
      });
      setSite((s) => ensureAdminSiteShape({ ...s, gallery: data.gallery }));
      setMsg('Saved.');
    } catch (e) {
      setErr(e.message || 'Save failed');
      try {
        const d = await fetchSite();
        setSite(ensureAdminSiteShape(d));
      } catch {
        /* ignore */
      }
    } finally {
      setSaving(false);
    }
  }

  if (!site || !site.gallery) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center text-neutral-600">Loading…</div>
    );
  }

  const { pageHeroImage, sectionLabel, sectionTitle, intro, items } = site.gallery;

  return (
    <>
      <header className="z-10 flex shrink-0 flex-wrap items-center justify-between gap-3 border-b border-neutral-200 bg-white px-4 py-3 md:px-8">
        <div>
          <p className="text-xs font-medium text-neutral-500">Site content</p>
          <h2 className="text-lg font-bold text-neutral-900 md:text-xl">Gallery</h2>
        </div>
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="rounded-lg bg-brand-forest px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save section'}
        </button>
      </header>

      <main className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto overscroll-y-contain px-4 py-6 md:px-8 md:py-8">
        {msg ? <p className="mb-4 text-sm text-green-700">{msg}</p> : null}
        {err ? <p className="mb-4 text-sm text-red-600">{err}</p> : null}

        <section className="mb-6 rounded-2xl border border-neutral-200 bg-white p-6 shadow-sm">
          <h2 className="mb-1 text-lg font-bold text-neutral-900">Page hero image</h2>
          <p className="mb-4 text-sm text-neutral-600">
            Shown behind the top banner on <span className="font-mono">/gallery</span>. If empty, the first
            album thumbnail is used.
          </p>

          <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
            <div className="aspect-[16/6] w-full max-w-md overflow-hidden rounded-lg border border-neutral-200 bg-neutral-50">
              {pageHeroImage ? (
                <img src={pageHeroImage} alt="" className="h-full w-full object-cover" />
              ) : (
                <div className="flex h-full min-h-[6rem] items-center justify-center text-xs text-neutral-400">
                  No image — first album thumbnail will be used
                </div>
              )}
            </div>

            <div className="flex-1 space-y-3">
              <label className={label}>
                Image URL
                <input
                  className={input}
                  value={pageHeroImage || ''}
                  onChange={(e) => updateGallery('pageHeroImage', e.target.value)}
                  placeholder="https://… or upload below"
                />
              </label>
              <label className={label}>
                Upload (Cloudinary)
                <input
                  type="file"
                  accept="image/*"
                  disabled={uploadingHero}
                  className={fileInput}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    e.target.value = '';
                    onHeroUpload(f);
                  }}
                />
                {uploadingHero ? (
                  <p className="mt-1 text-xs text-neutral-500">Uploading…</p>
                ) : null}
              </label>
              {pageHeroImage ? (
                <button
                  type="button"
                  onClick={() => updateGallery('pageHeroImage', '')}
                  className="text-xs font-medium text-neutral-600 underline decoration-neutral-300 hover:text-neutral-900"
                >
                  Clear hero image
                </button>
              ) : null}
            </div>
          </div>
        </section>

        <section className="rounded-2xl border border-neutral-200 bg-white p-6 shadow-sm">
          <h2 className="mb-4 text-lg font-bold text-neutral-900">Gallery section</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <label className={label}>
              Section label
              <input
                className={input}
                value={sectionLabel}
                onChange={(e) => updateGallery('sectionLabel', e.target.value)}
              />
            </label>
            <label className={`${label} sm:col-span-2`}>
              Section title
              <input
                className={input}
                value={sectionTitle}
                onChange={(e) => updateGallery('sectionTitle', e.target.value)}
              />
            </label>
            <label className={`${label} sm:col-span-2`}>
              Intro
              <textarea
                className={`${input} min-h-[5rem]`}
                value={intro}
                onChange={(e) => updateGallery('intro', e.target.value)}
              />
            </label>
          </div>

          <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <h3 className="text-base font-semibold text-neutral-800">Images</h3>
            <button
              type="button"
              onClick={openCreateModal}
              className="inline-flex items-center justify-center rounded-lg bg-brand-forest px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:opacity-90"
            >
              + New image
            </button>
          </div>

          {items.length === 0 ? (
            <p className="mt-5 text-center text-sm text-neutral-500">
              No images yet.{' '}
              <button type="button" onClick={openCreateModal} className="font-medium text-brand-olive hover:underline">
                Add one
              </button>
            </p>
          ) : (
            <div className="mt-5 overflow-x-auto rounded-xl border border-neutral-200 bg-white">
              <table className="w-full min-w-[min(100%,720px)] border-collapse text-left text-sm">
                <thead>
                  <tr className="border-b border-neutral-200 bg-neutral-50">
                    <th
                      scope="col"
                      className="w-24 px-3 py-3 text-xs font-semibold uppercase tracking-wide text-neutral-600"
                    >
                      Thumb
                    </th>
                    <th
                      scope="col"
                      className="max-w-[10rem] px-3 py-3 text-xs font-semibold uppercase tracking-wide text-neutral-600"
                    >
                      Title / purpose
                    </th>
                    <th
                      scope="col"
                      className="max-w-[10rem] px-3 py-3 text-xs font-semibold uppercase tracking-wide text-neutral-600"
                    >
                      Album
                    </th>
                    <th scope="col" className="px-3 py-3 text-xs font-semibold uppercase tracking-wide text-neutral-600">
                      Alt
                    </th>
                    <th
                      scope="col"
                      className="max-w-[12rem] px-3 py-3 text-xs font-semibold uppercase tracking-wide text-neutral-600"
                    >
                      URL
                    </th>
                    <th
                      scope="col"
                      className="w-[1%] whitespace-nowrap px-3 py-3 text-right text-xs font-semibold uppercase tracking-wide text-neutral-600"
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((row, i) => (
                    <tr key={row.id || i} className="border-b border-neutral-100 last:border-0 hover:bg-neutral-50/90">
                      <td className="align-middle px-3 py-2">
                        <div className="flex h-12 w-20 shrink-0 items-center justify-center overflow-hidden rounded-md bg-neutral-100">
                          {row.src ? (
                            <img src={row.src} alt="" className="h-full w-full object-cover" />
                          ) : (
                            <span className="text-[10px] text-neutral-400">—</span>
                          )}
                        </div>
                      </td>
                      <td className="max-w-[10rem] px-3 py-2 align-middle text-neutral-700">
                        <span className="line-clamp-2" title={row.title}>
                          {row.title?.trim() ? row.title : '—'}
                        </span>
                      </td>
                      <td className="max-w-[10rem] px-3 py-2 align-middle text-neutral-700">
                        <span className="line-clamp-2" title={row.album}>
                          {row.album?.trim() ? row.album : 'Uncategorized'}
                        </span>
                      </td>
                      <td className="max-w-[14rem] px-3 py-2 align-middle text-neutral-700">
                        <span className="line-clamp-2">{row.alt || '—'}</span>
                      </td>
                      <td className="max-w-[12rem] px-3 py-2 align-middle">
                        <span className="block truncate font-mono text-xs text-neutral-500" title={row.src}>
                          {row.src || '—'}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 align-middle text-right">
                        <div className="inline-flex flex-wrap items-center justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => openEditModal(row)}
                            className="rounded-lg border border-neutral-300 bg-white px-3 py-1.5 text-xs font-semibold text-neutral-800 hover:bg-neutral-50"
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            className="rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-50"
                            onClick={() => removeItem(row, i)}
                          >
                            Remove
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </main>

      <AdminModal
        open={itemModalOpen}
        title={itemModalMode === 'create' ? 'New gallery image' : 'Edit gallery image'}
        description={
          itemModalMode === 'edit' && editGalleryItemId
            ? `Item id: ${editGalleryItemId}`
            : 'Image URL (or upload), album, title, and optional alt text.'
        }
        error={modalErr}
        onClose={closeItemModal}
        footer={
          <div className="flex flex-wrap items-center justify-between gap-2">
            {itemModalMode === 'edit' ? (
              <button
                type="button"
                disabled={modalSaving}
                onClick={handleModalDelete}
                className={dangerBtnClass}
              >
                {modalSaving ? '…' : 'Remove from gallery'}
              </button>
            ) : (
              <span />
            )}
            <ModalFooter
              onCancel={closeItemModal}
              onSave={handleModalSave}
              saveLabel={itemModalMode === 'create' ? 'Add image' : 'Save changes'}
              saving={modalSaving}
            />
          </div>
        }
      >
        {draft ? (
          <div className="space-y-4">
            <AdminImagePreview src={draft.src} alt={draft.alt || ''} emptyLabel="Preview" />
            <label className={label}>
              Image URL
              <input
                className={input}
                value={draft.src ?? ''}
                onChange={(e) => {
                  setModalErr('');
                  setDraft((d) => (d ? { ...d, src: e.target.value } : d));
                }}
                placeholder="https://… or path after upload"
              />
              <span className="mt-1.5 block text-[11px] font-normal text-neutral-500">
                Paste a Cloudinary URL or upload below.
              </span>
            </label>
            <label className={label}>
              Upload (Cloudinary)
              <input
                type="file"
                accept="image/*"
                disabled={uploadingImage}
                className={fileInput}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  e.target.value = '';
                  onModalUpload(f);
                }}
              />
              {uploadingImage ? <p className="mt-1 text-xs text-neutral-500">Uploading…</p> : null}
            </label>
            {draft.src ? (
              <button
                type="button"
                onClick={() => {
                  setModalErr('');
                  setDraft((d) => (d ? { ...d, src: '' } : d));
                }}
                className="text-xs font-medium text-neutral-600 underline decoration-neutral-300 hover:text-neutral-900"
              >
                Clear image URL
              </button>
            ) : null}
            <GalleryForm
              item={draft}
              onChange={(next) => {
                setModalErr('');
                setDraft(next);
              }}
              onUploadImage={onModalUpload}
              albumOptions={albumOptions}
              hideImageBlock
            />
          </div>
        ) : null}
      </AdminModal>
    </>
  );
}
